import type {
  HydratedQuantityBreak,
  ItemUnit,
  PricingUnit,
} from "@forgepoint/shared";
import { formatItemUnit, formatPricingUnit } from "@forgepoint/shared";

/**
 * Builds a human-readable tooltip describing how a price was converted
 * from one pricing unit to the line unit.
 * @param params - Conversion details including original/converted prices and units
 * @returns Formatted string like "$500.00/MLF -> $10.00/ea (0.02 MLF per ea)"
 */
export function buildConversionTooltip(params: {
  originalPrice: string;
  pricingUnit: PricingUnit;
  convertedPrice: string;
  lineUnit: ItemUnit;
  conversionFactor: string;
}): string {
  const {
    originalPrice,
    pricingUnit,
    convertedPrice,
    lineUnit,
    conversionFactor,
  } = params;
  const pricingLabel = formatPricingUnit(pricingUnit);
  const lineLabel = formatItemUnit(lineUnit);
  const fmtOriginal = formatDollar(originalPrice);
  const fmtConverted = formatDollar(convertedPrice);

  return `${fmtOriginal}/${pricingLabel} \u2192 ${fmtConverted}/${lineLabel} (${conversionFactor} ${pricingLabel} per ${lineLabel})`;
}

/**
 * Returns a conversion tooltip string if the hydrated break indicates a unit
 * conversion occurred, otherwise returns null.
 * @param hydratedBreak - The hydrated quantity break (may be undefined)
 * @param lineUnit - The unit the line is priced in (may be null)
 * @returns Tooltip text or null if no conversion
 */
export function getConversionTooltipText(
  hydratedBreak: HydratedQuantityBreak | undefined,
  lineUnit: ItemUnit | null | undefined,
): string | null {
  if (
    !hydratedBreak?.pricingUnit ||
    !lineUnit ||
    !hydratedBreak.convertedFromUnitPrice ||
    !hydratedBreak.conversionFactor
  ) {
    return null;
  }

  return buildConversionTooltip({
    originalPrice: hydratedBreak.convertedFromUnitPrice,
    pricingUnit: hydratedBreak.pricingUnit,
    convertedPrice: hydratedBreak.unitPrice ?? "0",
    lineUnit,
    conversionFactor: hydratedBreak.conversionFactor,
  });
}

/**
 * Formats a numeric string as a dollar amount with exactly 2 decimal places.
 * @param value - The numeric string to format
 * @returns Dollar-formatted string (e.g. "$5.00")
 */
function formatDollar(value: string): string {
  const num = parseFloat(value);
  if (isNaN(num)) {
    return `$${value}`;
  }
  return `$${num.toFixed(2)}`;
}
